import {
  isKnownSlotAssignmentBlockerCode,
  slotAssignmentBlockerLabel,
} from './slot-assignment-override';
import type { MasterApplyMode } from './master-roster-apply-identity';

/** Presentation opt-in only; global rows never enable this slice. */
export const ROSTER_APPLY_CLARITY_FLAG = 'roster_apply_clarity_v1';
export function isTenantScopedRosterApplyClarityFlag(key: string | null | undefined): boolean {
  return key === ROSTER_APPLY_CLARITY_FLAG;
}

export interface MasterApplyClarityProbe {
  lineageCount?: number;
  eActiveCount?: number;
  retirementCount?: number;
  windowMismatch?: boolean;
  windowOccurrenceCount?: number;
  disableAppend: boolean;
  disableAppendMissing: boolean;
  copy: string | null;
}

export function resolveRecommendedMasterApply(
  probe: MasterApplyClarityProbe,
  allowAddMissing: boolean,
): { mode: MasterApplyMode | null; explanation: string | null } {
  if (probe.disableAppend && probe.disableAppendMissing)
    return { mode: null, explanation: probe.copy };
  const hasLineage = (probe.lineageCount ?? probe.eActiveCount ?? 0) > 0 || probe.disableAppend;
  if (hasLineage && !allowAddMissing) {
    return {
      mode: null,
      explanation:
        'This Master already has shifts in this draft, including any cancelled shifts. Adding missing shifts is not enabled for your organisation. Review the existing work on Dispatch, or use Advanced to rebuild this Master’s shifts.',
    };
  }
  if (hasLineage) {
    return probe.disableAppendMissing
      ? { mode: null, explanation: probe.copy }
      : {
          mode: 'APPEND_MISSING',
          explanation:
            'Only missing Master shifts will be added. Existing work stays in place; cancelled shifts stay cancelled unless you choose to restore them.',
        };
  }
  return probe.disableAppend
    ? { mode: null, explanation: probe.copy }
    : {
        mode: 'APPEND',
        explanation:
          'Master shifts will be added alongside existing work. Exact matching hand-made shifts can cover Master occurrences.',
      };
}

export function masterApplyVacancyReason(vacancy: Record<string, unknown>): string {
  const codes = Array.isArray(vacancy.blockerCodes) ? vacancy.blockerCodes : [vacancy.reason];
  const labels = codes
    .filter(
      (code): code is string => typeof code === 'string' && isKnownSlotAssignmentBlockerCode(code),
    )
    .map(slotAssignmentBlockerLabel);
  return labels.length
    ? [...new Set(labels)].join('; ')
    : 'The intended worker could not be assigned. Review worker availability and restrictions on Dispatch.';
}

/** Only the abandonment ledger's compensation receipt identifies these rows.
 * Cancelled rows outside that receipt remain intentional lineage.
 */
export function abandonmentCompensatedShiftIds(
  applications: readonly { id: string; status: string; result_payload: unknown }[],
  shifts: readonly { id: string; status: string | null }[],
  slots: readonly { shift_id: string | null; status: string | null }[],
): Set<string> {
  const cancelled = new Set(
    shifts.filter((row) => row.status === 'CANCELLED').map((row) => row.id),
  );
  const activeSlotParents = new Set(
    slots.filter((row) => row.status !== 'CANCELLED').map((row) => row.shift_id),
  );
  const compensated = new Set<string>();
  for (const application of applications) {
    if (
      application.status !== 'ABANDONED' ||
      !application.result_payload ||
      typeof application.result_payload !== 'object'
    )
      continue;
    const receipt = application.result_payload as Record<string, unknown>;
    if (receipt.status !== 'ABANDONED' || !Array.isArray(receipt.compensatedShiftIds)) continue;
    for (const id of receipt.compensatedShiftIds) {
      if (typeof id === 'string' && cancelled.has(id) && !activeSlotParents.has(id))
        compensated.add(id);
    }
  }
  return compensated;
}
